import { toast } from '#src/base/toast/toast.tsx';
import { useCallback, useEffect, useState } from 'react';

interface UseClipboardProps {
  /** The duration where the clipboard state will be 'copied' before allowing user to copy again. */
  durationInMs: number;
}

interface UseClipboardReturn {
  /** Whether the text has been copied to the clipboard. */
  copied: boolean;
  /** Function to copy text to the clipboard. */
  copyToClipboard: (text: string) => Promise<void>;
}

/**
 * A reusable hook to copy text to the clipboard.
 * Handles errors and notifies user if browser does not support clipboard API.
 * Use copied state to provide feedback to user that text has been copied.
 * If clipboard API is not supported or any error occurs, an error toast will be shown.
 */
export function useClipboard({ durationInMs }: UseClipboardProps): UseClipboardReturn {
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), durationInMs);
      return () => clearTimeout(timer);
    }
  }, [copied, durationInMs]);

  const copyToClipboard = useCallback(
    async (text: string) => {
      // Avoid creating and clearing more timeouts if user is still within copy duration.
      if (copied) {
        return;
      }

      // If browser does not support clipboard API, show toast and report it as a warning.
      if (!navigator?.clipboard?.writeText) {
        console.warn('User browser does not support copy to clipboard');
        toast.error('Browser does not support copying to clipboard. Please copy manually.');
        return;
      }

      // Attempt to copy text to clipboard, show toast and report it if it does not work.
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
      } catch (error) {
        console.error('Failed to copy to clipboard', error);
        toast.error('Failed to copy to clipboard. Please copy manually.');
        setCopied(false);
      }
    },
    [copied],
  );

  return {
    copied,
    copyToClipboard,
  };
}
